import { log } from '../utils/logger';

/**
 * Tournaments API module
 * Handles all tournament-related API operations
 */

import { db, supabase } from '../lib/supabase';
import { createAPIMethod } from './base';

/**
 * Tournament API namespace
 */
export const tournamentAPI = {
  /**
   * Create a new tournament
   * @param {Object} data - Tournament data
   * @returns {Promise<APIResponse>}
   */
  create: createAPIMethod(
    (data) => db.createTournament(data),
    { operation: 'Create tournament' }
  ),

  /**
   * Update an existing tournament
   * @param {string} id - Tournament ID
   * @param {Object} data - Updated tournament data
   * @returns {Promise<APIResponse>}
   */
  update: createAPIMethod(
    (id, data) => db.updateTournament(id, data),
    { operation: 'Update tournament' }
  ),

  /**
   * Delete a tournament
   * @param {string} id - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  delete: createAPIMethod(
    async (id) => {
      return await supabase
        .from('tournaments')
        .delete()
        .eq('id', id);
    },
    { operation: 'Delete tournament' }
  ),

  /**
   * Get a single tournament by ID
   * @param {string} id - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (id) => {
      return await supabase
        .from('tournaments')
        .select('*')
        .eq('id', id)
        .single();
    },
    { operation: 'Get tournament' }
  ),

  /**
   * Get all tournaments with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('tournaments')
        .select('*');

      // Apply filters
      if (filters.organizerId) {
        query = query.eq('organizer_id', filters.organizerId);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.sportActivity) {
        query = query.eq('sport_activity', filters.sportActivity);
      }
      if (filters.division) {
        query = query.eq('division', filters.division);
      }
      if (filters.dateFrom) {
        query = query.gte('start_date', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('end_date', filters.dateTo);
      }
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      // Add ordering - most recent tournaments first by end date
      query = query.order('end_date', { ascending: false });

      return await query;
    },
    { operation: 'Get tournaments' }
  ),

  /**
   * Get tournaments managed by a specific user
   * @param {string} userId - Organizer user ID
   * @returns {Promise<APIResponse>}
   */
  getByOrganizer: createAPIMethod(
    async (userId) => {
      return await supabase
        .from('tournaments')
        .select(`
          *,
          tournament_teams (
            id,
            teams (
              id, name
            )
          )
        `)
        .eq('organizer_id', userId)
        .order('start_date', { ascending: true });
    },
    { operation: 'Get organizer tournaments' }
  ),

  /**
   * Get tournament statistics
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  getStatistics: createAPIMethod(
    async (tournamentId) => {
      // Get various statistics for the tournament
      const [
        teamsResult,
        gamesResult,
        reservationsResult
      ] = await Promise.allSettled([
        supabase
          .from('tournament_teams')
          .select('id, teams(id, name, division)')
          .eq('tournament_id', tournamentId),
        supabase
          .from('games')
          .select('id, status, starts_at')
          .eq('tournament_id', tournamentId),
        supabase
          .from('reservations')
          .select('id, status')
          .in('game_id', 
            supabase
              .from('games')
              .select('id')
              .eq('tournament_id', tournamentId)
          )
      ]);

      const teams = teamsResult.status === 'fulfilled' 
        ? teamsResult.value.data || []
        : [];
      const games = gamesResult.status === 'fulfilled' 
        ? gamesResult.value.data || []
        : [];
      const reservations = reservationsResult.status === 'fulfilled' 
        ? reservationsResult.value.data || []
        : [];

      // Process team statistics
      const teamStats = {
        total: teams.length,
        byDivision: {}
      };

      teams.forEach(t => {
        const division = t.teams?.division || 'Unspecified';
        teamStats.byDivision[division] = (teamStats.byDivision[division] || 0) + 1;
      });

      // Process game statistics
      const now = new Date();
      const gameStats = {
        total: games.length,
        scheduled: games.filter(g => g.status === 'scheduled').length,
        completed: games.filter(g => g.status === 'completed').length,
        upcoming: games.filter(g => new Date(g.starts_at) > now).length,
        past: games.filter(g => new Date(g.starts_at) <= now).length
      };

      // Process reservation statistics
      const reservationStats = {
        total: reservations.length,
        pending: 0,
        confirmed: 0,
        completed: 0,
        cancelled: 0
      };

      reservations.forEach(r => {
        if (reservationStats[r.status] !== undefined) {
          reservationStats[r.status]++;
        }
      });

      return {
        teams: teamStats,
        games: gameStats,
        reservations: reservationStats
      };
    },
    { operation: 'Get tournament statistics' }
  ),

  /**
   * Add teams to a tournament
   * @param {string} tournamentId - Tournament ID
   * @param {Array} teamIds - Array of team IDs
   * @param {string} organizerId - Organizer user ID
   * @returns {Promise<APIResponse>}
   */
  addTeams: createAPIMethod(
    async (tournamentId, teamIds, organizerId) => {
      const tournamentTeams = teamIds.map(teamId => ({
        tournament_id: tournamentId,
        team_id: teamId,
        registered_by: organizerId,
        registered_at: new Date().toISOString()
      }));

      return await supabase
        .from('tournament_teams')
        .insert(tournamentTeams)
        .select(`
          *,
          teams (
            id, name, division, sport_activity
          )
        `);
    },
    { operation: 'Add teams to tournament' }
  ),

  /**
   * Remove a team from a tournament
   * @param {string} tournamentId - Tournament ID
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  removeTeam: createAPIMethod(
    async (tournamentId, teamId) => {
      return await supabase
        .from('tournament_teams')
        .delete()
        .eq('tournament_id', tournamentId)
        .eq('team_id', teamId);
    },
    { operation: 'Remove team from tournament' }
  ),

  /**
   * Get tournament teams
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  getTeams: createAPIMethod(
    async (tournamentId) => {
      return await supabase
        .from('tournament_teams')
        .select(`
          *,
          teams (
            id, name, division, sport_activity, city, state
          )
        `)
        .eq('tournament_id', tournamentId)
        .order('registered_at', { ascending: true });
    },
    { operation: 'Get tournament teams' }
  ),

  /**
   * Get teams in tournament (for team managers) - uses RPC function to bypass RLS
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  getTeamsInTournament: createAPIMethod(
    async (tournamentId) => {
      log.info('🔍 Getting teams in tournament via RPC:', tournamentId);
      
      const response = await supabase
        .rpc('get_tournament_teams', { tournament_id_param: tournamentId });
      
      if (response.error) {
        log.error('❌ Error getting tournament teams:', response.error);
        throw response.error;
      }
      
      log.info('✅ Found teams in tournament:', response.data?.length || 0);
      
      // Transform the data to match the expected format
      const transformedData = response.data?.map(team => ({
        team_id: team.team_id,
        teams: {
          id: team.team_id,
          name: team.team_name,
          sport_activity: team.sport_activity,
          company: team.company
        }
      })) || [];
      
      return transformedData;
    },
    { operation: 'Get teams in tournament' }
  ),

  /**
   * Get tournament locations
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  getTournamentLocations: createAPIMethod(
    async (tournamentId) => {
      return await supabase
        .from('tournament_locations')
        .select('*, game_locations(name, address_line1, city, state)')
        .eq('tournament_id', tournamentId)
        .order('is_main_location', { ascending: false });
    },
    { operation: 'Get tournament locations' }
  ),

  /**
   * Get tournament games
   * @param {string} tournamentId - Tournament ID
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getGames: createAPIMethod(
    async (tournamentId, filters = {}) => {
      let query = supabase
        .from('games')
        .select(`
          *,
          home_team:teams!home_team_id (
            id, name
          ),
          away_team:teams!away_team_id (
            id, name
          ),
          game_locations (
            id, name, address_line1, city, state
          ),
          reservations (
            id, status, reserved_at,
            venues (
              id, name, city, state
            )
          )
        `)
        .eq('tournament_id', tournamentId);

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.teamId) {
        query = query.or(`home_team_id.eq.${filters.teamId},away_team_id.eq.${filters.teamId}`);
      }
      if (filters.dateFrom) {
        query = query.gte('starts_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('starts_at', filters.dateTo);
      }

      // Add ordering
      query = query.order('starts_at', { ascending: true });

      return await query;
    },
    { operation: 'Get tournament games' }
  ),

  /**
   * Update tournament status
   * @param {string} id - Tournament ID
   * @param {string} status - New status
   * @returns {Promise<APIResponse>}
   */
  updateStatus: createAPIMethod(
    async (id, status) => {
      const validStatuses = ['draft', 'open', 'in-progress', 'completed', 'cancelled'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}`);
      }

      return await supabase
        .from('tournaments')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Update tournament status' }
  ),

  /**
   * Generate tournament bracket
   * @param {string} tournamentId - Tournament ID
   * @param {Object} options - Bracket generation options
   * @returns {Promise<APIResponse>}
   */
  generateBracket: createAPIMethod(
    async (tournamentId, options = {}) => {
      const { format = 'single-elimination', seedByDivision = true } = options;

      // Get tournament teams
      const { data: teams } = await supabase
        .from('tournament_teams')
        .select(`
          *,
          teams (
            id, name, division, sport_activity
          )
        `)
        .eq('tournament_id', tournamentId);

      if (!teams || teams.length < 2) {
        throw new Error('Need at least 2 teams to generate bracket');
      }

      // Group teams by division if needed
      let brackets = {};
      if (seedByDivision) {
        teams.forEach(team => {
          const division = team.teams.division || 'Open';
          if (!brackets[division]) {
            brackets[division] = [];
          }
          brackets[division].push(team);
        });
      } else {
        brackets['Main'] = teams;
      }

      // Generate games for each bracket
      const allGames = [];
      for (const [division, divisionTeams] of Object.entries(brackets)) {
        if (divisionTeams.length < 2) continue;

        // Simple bracket generation - pair teams
        const games = [];
        const shuffledTeams = [...divisionTeams].sort(() => Math.random() - 0.5);
        
        for (let i = 0; i < shuffledTeams.length; i += 2) {
          if (shuffledTeams[i + 1]) {
            games.push({
              tournament_id: tournamentId,
              home_team_id: shuffledTeams[i].team_id,
              away_team_id: shuffledTeams[i + 1].team_id,
              status: 'scheduled',
              round: 1,
              division: division
            });
          }
        }
        allGames.push(...games);
      }

      // Insert games
      if (allGames.length > 0) {
        await supabase
          .from('games')
          .insert(allGames);
      }

      return {
        gamesGenerated: allGames.length,
        brackets: Object.keys(brackets),
        format
      };
    },
    { operation: 'Generate tournament bracket' }
  ),

  /**
   * Get upcoming tournaments
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getUpcoming: createAPIMethod(
    async (filters = {}) => {
      const now = new Date().toISOString();
      
      let query = supabase
        .from('tournaments')
        .select('*')
        .gte('start_date', now)
        .eq('status', 'open');

      // Apply additional filters
      if (filters.sportActivity) {
        query = query.eq('sport_activity', filters.sportActivity);
      }
      if (filters.division) {
        query = query.eq('division', filters.division);
      }

      query = query
        .order('start_date', { ascending: true })
        .limit(10);

      return await query;
    },
    { operation: 'Get upcoming tournaments' }
  ),

  /**
   * Register team for tournament
   * @param {string} tournamentId - Tournament ID
   * @param {string} teamId - Team ID
   * @param {string} managerId - Team manager ID
   * @returns {Promise<APIResponse>}
   */
  registerTeam: createAPIMethod(
    async (tournamentId, teamId, managerId) => {
      // Check if team is already registered
      const { data: existing } = await supabase
        .from('tournament_teams')
        .select('id')
        .eq('tournament_id', tournamentId)
        .eq('team_id', teamId)
        .single();

      if (existing) {
        throw new Error('Team is already registered for this tournament');
      }

      // Register the team
      return await supabase
        .from('tournament_teams')
        .insert({
          tournament_id: tournamentId,
          team_id: teamId,
          registered_by: managerId,
          registered_at: new Date().toISOString()
        })
        .select(`
          *,
          teams (
            id, name, division
          )
        `)
        .single();
    },
    { operation: 'Register team for tournament' }
  ),

  /**
   * Get tournament divisions (unique values)
   * @returns {Promise<APIResponse>}
   */
  getDivisions: createAPIMethod(
    async () => {
      const { data, error } = await supabase
        .from('tournaments')
        .select('division')
        .not('division', 'is', null);

      if (error) throw error;

      // Get unique divisions
      const divisions = [...new Set(data.map(t => t.division))].filter(Boolean).sort();

      return divisions;
    },
    { operation: 'Get tournament divisions' }
  ),

  /**
   * Get tournament locations
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  getLocations: createAPIMethod(
    async (tournamentId) => {
      return await supabase
        .from('tournament_locations')
        .select(`
          *,
          game_locations (
            id, name, address_line1, city, state
          )
        `)
        .eq('tournament_id', tournamentId)
        .order('is_main_location', { ascending: false });
    },
    { operation: 'Get tournament locations' }
  ),

  /**
   * Create tournament locations
   * @param {string} tournamentId - Tournament ID
   * @param {Array} locationIds - Array of location IDs
   * @param {string} mainLocationId - Main location ID
   * @param {string} userId - User ID creating the locations
   * @returns {Promise<APIResponse>}
   */
  createLocations: createAPIMethod(
    async (tournamentId, locationIds, mainLocationId, _userId) => {
      if (!locationIds || locationIds.length === 0) {
        return [];
      }

      const locationInserts = locationIds.map(locationId => ({
        tournament_id: tournamentId,
        location_id: locationId,
        is_main_location: locationId === mainLocationId
      }));

      return await supabase
        .from('tournament_locations')
        .insert(locationInserts)
        .select();
    },
    { operation: 'Create tournament locations' }
  ),

  /**
   * Update tournament locations
   * @param {string} tournamentId - Tournament ID
   * @param {Array} locationIds - Array of location IDs
   * @param {string} mainLocationId - Main location ID
   * @param {string} userId - User ID updating the locations
   * @returns {Promise<APIResponse>}
   */
  updateLocations: createAPIMethod(
    async (tournamentId, locationIds, mainLocationId, _userId) => {
      // First, delete existing tournament locations
      await supabase
        .from('tournament_locations')
        .delete()
        .eq('tournament_id', tournamentId);

      // Then insert new locations if any
      if (locationIds && locationIds.length > 0) {
        const locationInserts = locationIds.map(locationId => ({
          tournament_id: tournamentId,
          location_id: locationId,
          is_main_location: locationId === mainLocationId
        }));

        return await supabase
          .from('tournament_locations')
          .insert(locationInserts)
          .select();
      }

      return [];
    },
    { operation: 'Update tournament locations' }
  ),

  /**
   * Get tournament divisions (tournament_divisions table)
   * @param {string} tournamentId - Optional tournament ID to filter divisions for specific tournament
   * @returns {Promise<APIResponse>}
   */
  getTournamentDivisions: createAPIMethod(
    async (tournamentId) => {
      if (tournamentId) {
        // Get divisions for a specific tournament
        const result = await supabase
          .from('tournament_tournament_divisions')
          .select(`
            *,
            tournament_divisions (
              id, name, description, skill_level, display_order
            )
          `)
          .eq('tournament_id', tournamentId)
          .order('tournament_divisions(skill_level)')
          .order('tournament_divisions(display_order)');

        // Transform to return just the division data
        if (result.data) {
          return result.data.map(item => ({
            id: item.tournament_divisions.id,
            name: item.tournament_divisions.name,
            description: item.tournament_divisions.description,
            skill_level: item.tournament_divisions.skill_level,
            display_order: item.tournament_divisions.display_order
          }));
        }
        return result;
      }

      // Get all active divisions
      return await supabase
        .from('tournament_divisions')
        .select('*')
        .eq('is_active', true)
        .order('skill_level')
        .order('display_order');
    },
    { operation: 'Get tournament divisions' }
  ),

  /**
   * Create tournament division
   * @param {Object} divisionData - Division data
   * @returns {Promise<APIResponse>}
   */
  createTournamentDivision: createAPIMethod(
    async (divisionData) => {
      return await supabase
        .from('tournament_divisions')
        .insert([divisionData])
        .select()
        .single();
    },
    { operation: 'Create tournament division' }
  ),

  /**
   * Get tournament types
   * @returns {Promise<APIResponse>}
   */
  getTournamentTypes: createAPIMethod(
    async () => {
      return await supabase
        .from('tournament_types')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
    },
    { operation: 'Get tournament types' }
  ),

  /**
   * Create tournament type
   * @param {Object} typeData - Type data
   * @returns {Promise<APIResponse>}
   */
  createTournamentType: createAPIMethod(
    async (typeData) => {
      return await supabase
        .from('tournament_types')
        .insert([typeData])
        .select()
        .single();
    },
    { operation: 'Create tournament type' }
  ),

  /**
   * Add divisions to tournament
   * @param {string} tournamentId - Tournament ID
   * @param {Array} divisionIds - Array of division IDs
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  addDivisions: createAPIMethod(
    async (tournamentId, divisionIds, userId) => {
      if (!divisionIds || divisionIds.length === 0) {
        return [];
      }

      const divisionInserts = divisionIds.map(divisionId => ({
        tournament_id: tournamentId,
        division_id: divisionId,
        created_by: userId
      }));

      return await supabase
        .from('tournament_tournament_divisions')
        .insert(divisionInserts)
        .select();
    },
    { operation: 'Add tournament divisions' }
  ),

  /**
   * Update tournament divisions
   * @param {string} tournamentId - Tournament ID
   * @param {Array} divisionIds - Array of division IDs
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  updateDivisions: createAPIMethod(
    async (tournamentId, divisionIds, userId) => {
      // First, delete existing divisions
      await supabase
        .from('tournament_tournament_divisions')
        .delete()
        .eq('tournament_id', tournamentId);

      // Then add new divisions if any
      if (divisionIds && divisionIds.length > 0) {
        const divisionInserts = divisionIds.map(divisionId => ({
          tournament_id: tournamentId,
          division_id: divisionId,
          created_by: userId
        }));

        return await supabase
          .from('tournament_tournament_divisions')
          .insert(divisionInserts)
          .select();
      }

      return [];
    },
    { operation: 'Update tournament divisions' }
  ),

  /**
   * Bulk create divisions for schedule import
   * Creates divisions that don't exist yet and links them to the tournament
   * @param {string} tournamentId - Tournament ID
   * @param {Array<{divisionName: string, gameCount: number}>} newDivisions - Array of new division objects
   * @param {string} userId - User ID creating the divisions
   * @returns {Promise<APIResponse>} - Object with created/existing divisions and their IDs
   */
  bulkCreateDivisions: createAPIMethod(
    async (tournamentId, newDivisions, userId) => {
      if (!newDivisions || newDivisions.length === 0) {
        return { created: [], existing: [], divisionMap: {} };
      }

      const divisionNames = newDivisions.map(d => d.divisionName);
      const divisionMap = {}; // Maps name to ID
      const created = [];
      const existing = [];

      // Check which divisions already exist in tournament_divisions table
      const { data: existingDivisions } = await supabase
        .from('tournament_divisions')
        .select('id, name')
        .in('name', divisionNames);

      // Track which already exist
      const existingByName = {};
      (existingDivisions || []).forEach(div => {
        existingByName[div.name.toLowerCase()] = div;
      });

      // Create divisions that don't exist
      const toCreate = [];
      for (const div of newDivisions) {
        const key = div.divisionName.toLowerCase();
        if (existingByName[key]) {
          divisionMap[div.divisionName] = existingByName[key].id;
          existing.push({ ...existingByName[key], gameCount: div.gameCount });
        } else {
          toCreate.push({
            name: div.divisionName,
            description: `Auto-created from schedule import for tournament`,
            is_active: true
          });
        }
      }

      // Insert new divisions
      if (toCreate.length > 0) {
        const { data: createdDivisions, error: createError } = await supabase
          .from('tournament_divisions')
          .insert(toCreate)
          .select();

        if (createError) {
          log.error('Error creating divisions:', createError);
          throw createError;
        }

        // Map created divisions
        (createdDivisions || []).forEach(div => {
          divisionMap[div.name] = div.id;
          const original = newDivisions.find(d => d.divisionName === div.name);
          created.push({ ...div, gameCount: original?.gameCount || 0 });
        });
      }

      // Link all divisions to tournament if not already linked
      const divisionIdsToLink = Object.values(divisionMap);

      // Check which are already linked
      const { data: existingLinks } = await supabase
        .from('tournament_tournament_divisions')
        .select('division_id')
        .eq('tournament_id', tournamentId)
        .in('division_id', divisionIdsToLink);

      const existingLinkIds = new Set((existingLinks || []).map(l => l.division_id));
      const newLinkIds = divisionIdsToLink.filter(id => !existingLinkIds.has(id));

      // Create new links
      if (newLinkIds.length > 0) {
        const linkInserts = newLinkIds.map(divisionId => ({
          tournament_id: tournamentId,
          division_id: divisionId,
          created_by: userId
        }));

        const { error: linkError } = await supabase
          .from('tournament_tournament_divisions')
          .insert(linkInserts);

        if (linkError) {
          log.error('Error linking divisions to tournament:', linkError);
          // Don't throw - divisions were created, just linking failed
        }
      }

      log.info(`Division auto-creation complete: ${created.length} created, ${existing.length} already existed`);

      return {
        created,
        existing,
        divisionMap
      };
    },
    { operation: 'Bulk create divisions for schedule import' }
  ),

  /**
   * Get swap impact preview - counts affected records before swap
   * @param {string} tournamentId - Tournament ID
   * @param {string} oldTeamId - Team to be replaced
   * @returns {Promise<APIResponse>}
   */
  getSwapImpact: createAPIMethod(
    async (tournamentId, oldTeamId) => {
      // Count games where team is home or away
      const [gamesAsHomeResult, gamesAsAwayResult, gamesResult] = await Promise.all([
        supabase
          .from('games')
          .select('id', { count: 'exact', head: true })
          .eq('tournament_id', tournamentId)
          .eq('home_team_id', oldTeamId),
        supabase
          .from('games')
          .select('id', { count: 'exact', head: true })
          .eq('tournament_id', tournamentId)
          .eq('away_team_id', oldTeamId),
        supabase
          .from('games')
          .select('id')
          .eq('tournament_id', tournamentId)
      ]);

      // Get game IDs for reservation count
      const gameIds = (gamesResult.data || []).map(g => g.id);

      let reservationsCount = 0;
      if (gameIds.length > 0) {
        const reservationsResult = await supabase
          .from('reservations')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', oldTeamId)
          .in('game_id', gameIds);
        reservationsCount = reservationsResult.count || 0;
      }

      return {
        tournamentTeams: 1,
        gamesAsHome: gamesAsHomeResult.count || 0,
        gamesAsAway: gamesAsAwayResult.count || 0,
        reservations: reservationsCount
      };
    },
    { operation: 'Get swap impact preview' }
  ),

  /**
   * Swap a team in a tournament (calls RPC for atomic operation)
   * @param {string} tournamentId - Tournament ID
   * @param {string} oldTeamId - Team to be replaced
   * @param {string} newTeamId - Replacement team
   * @returns {Promise<APIResponse>}
   */
  swapTeam: createAPIMethod(
    async (tournamentId, oldTeamId, newTeamId) => {
      const { data, error } = await supabase.rpc('swap_tournament_team', {
        p_tournament_id: tournamentId,
        p_old_team_id: oldTeamId,
        p_new_team_id: newTeamId
      });

      if (error) {
        log.error('Error swapping team:', error);
        throw error;
      }

      log.info('Team swap successful:', data);
      return data;
    },
    { operation: 'Swap tournament team' }
  )
};